import { useCallback, useEffect, useMemo, useState } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { useSucursal } from '../../utils/sucursalContext';

type Estado = 'pendiente' | 'en_progreso' | 'completada' | 'cancelada';
type Coach = { id: string; nombre: string; email: string; tipo_staff: string };
type Actividad = {
  id: string;
  coach_admin_id: string;
  titulo: string;
  descripcion: string | null;
  fecha_limite: string | null;
  estado: Estado;
  completada_at: string | null;
  created_at: string;
};

const labels: Record<Estado, string> = {
  pendiente: 'Pendiente',
  en_progreso: 'En progreso',
  completada: 'Completada',
  cancelada: 'Cancelada',
};

export default function ActividadesCoachesPage() {
  const { profile } = useAuth();
  const { sucursalActiva } = useSucursal();
  const isManager = profile?.role !== 'staff';
  const canSupervise = isManager || profile?.supervisorCoaches === true;
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [items, setItems] = useState<Actividad[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'todas' | Estado>('todas');
  const [form, setForm] = useState({ coachId: '', titulo: '', descripcion: '', fechaLimite: '' });

  const load = useCallback(async () => {
    if (!sucursalActiva?.gimnasio_id) return;
    setLoading(true);
    setError(null);
    try {
      const [coachResult, activityResult] = await Promise.all([
        supabase.from('usuarios_admin').select('id,nombre,email,tipo_staff')
          .eq('gimnasio_id', sucursalActiva.gimnasio_id).eq('role', 'staff')
          .in('tipo_staff', ['coach_interno', 'coach_externo']).eq('activo', true).order('nombre'),
        supabase.from('coach_actividades').select('id,coach_admin_id,titulo,descripcion,fecha_limite,estado,completada_at,created_at')
          .eq('gimnasio_id', sucursalActiva.gimnasio_id).order('created_at', { ascending: false }),
      ]);
      if (coachResult.error) throw coachResult.error;
      if (activityResult.error) throw activityResult.error;
      const coachRows = (coachResult.data || []) as Coach[];
      setCoaches(coachRows);
      setItems((activityResult.data || []) as Actividad[]);
      setForm((old) => ({ ...old, coachId: old.coachId || coachRows[0]?.id || '' }));
    } catch (err: any) {
      setError(err?.message || 'No se pudieron cargar las actividades.');
    } finally {
      setLoading(false);
    }
  }, [sucursalActiva?.gimnasio_id]);

  useEffect(() => { void load(); }, [load]);

  const visible = useMemo(
    () => filter === 'todas' ? items : items.filter((item) => item.estado === filter),
    [filter, items],
  );
  const coachNames = useMemo(() => new Map(coaches.map((coach) => [coach.id, coach.nombre])), [coaches]);
  const stats = useMemo(() => ({
    pendientes: items.filter((item) => item.estado === 'pendiente').length,
    progreso: items.filter((item) => item.estado === 'en_progreso').length,
    completadas: items.filter((item) => item.estado === 'completada').length,
    vencidas: items.filter((item) => item.estado !== 'completada' && item.estado !== 'cancelada' && item.fecha_limite && item.fecha_limite < new Date().toISOString().slice(0, 10)).length,
  }), [items]);

  const createActivity = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.coachId || !form.titulo.trim() || !sucursalActiva?.gimnasio_id) return;
    setSaving(true);
    setError(null);
    const { error: insertError } = await supabase.from('coach_actividades').insert({
      gimnasio_id: sucursalActiva.gimnasio_id,
      sucursal_id: sucursalActiva.id || null,
      coach_admin_id: form.coachId,
      titulo: form.titulo.trim(),
      descripcion: form.descripcion.trim() || null,
      fecha_limite: form.fechaLimite || null,
    });
    setSaving(false);
    if (insertError) return setError(insertError.message);
    setForm((old) => ({ ...old, titulo: '', descripcion: '', fechaLimite: '' }));
    await load();
  };

  const updateStatus = async (id: string, estado: Estado) => {
    setError(null);
    const { error: updateError } = await supabase.from('coach_actividades').update({ estado }).eq('id', id);
    if (updateError) return setError(updateError.message);
    setItems((old) => old.map((item) => item.id === id ? { ...item, estado } : item));
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div><h1 className="text-3xl font-bold text-gray-900">{canSupervise ? 'Supervisión de coaches' : 'Mis actividades'}</h1><p className="text-gray-600 mt-1">Seguimiento de trabajos asignados, pendientes y completados.</p></div>
          <button onClick={() => void load()} className="px-4 py-2 border border-gray-300 rounded-lg text-sm"><i className="ri-refresh-line mr-2" />Actualizar</button>
        </div>

        {error && <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-red-700">{error}</div>}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            ['Pendientes', stats.pendientes, 'text-amber-600'], ['En progreso', stats.progreso, 'text-blue-600'],
            ['Completadas', stats.completadas, 'text-green-600'], ['Vencidas', stats.vencidas, 'text-red-600'],
          ].map(([label, value, color]) => <div key={String(label)} className="bg-white border rounded-xl p-5"><p className="text-sm text-gray-500">{label}</p><p className={`text-3xl font-bold mt-1 ${color}`}>{value}</p></div>)}
        </div>

        {canSupervise && <form onSubmit={createActivity} className="bg-white border rounded-xl p-5 space-y-4">
          <h2 className="text-lg font-semibold">Asignar actividad</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <label className="text-sm font-medium">Coach<select value={form.coachId} onChange={(e) => setForm({ ...form, coachId: e.target.value })} className="mt-1 w-full px-3 py-2.5 border rounded-lg" required><option value="">Seleccionar</option>{coaches.map((coach) => <option key={coach.id} value={coach.id}>{coach.nombre} · {coach.tipo_staff === 'coach_externo' ? 'Externo' : 'Interno'}</option>)}</select></label>
            <label className="text-sm font-medium">Fecha límite<input type="date" value={form.fechaLimite} onChange={(e) => setForm({ ...form, fechaLimite: e.target.value })} className="mt-1 w-full px-3 py-2.5 border rounded-lg" /></label>
          </div>
          <label className="block text-sm font-medium">Título<input value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} maxLength={160} className="mt-1 w-full px-3 py-2.5 border rounded-lg" required /></label>
          <label className="block text-sm font-medium">Descripción<textarea value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} rows={3} className="mt-1 w-full px-3 py-2.5 border rounded-lg" /></label>
          <div className="flex justify-end"><button disabled={saving || coaches.length === 0} className="px-5 py-2.5 bg-teal-600 text-white rounded-lg disabled:opacity-50">{saving ? 'Asignando…' : 'Asignar actividad'}</button></div>
        </form>}

        <div className="bg-white border rounded-xl overflow-hidden">
          <div className="p-4 border-b flex items-center justify-between gap-3"><h2 className="font-semibold">Actividades</h2><select value={filter} onChange={(e) => setFilter(e.target.value as typeof filter)} className="px-3 py-2 border rounded-lg text-sm"><option value="todas">Todas</option>{Object.entries(labels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></div>
          {loading ? <div className="p-10 text-center text-gray-500">Cargando actividades…</div> : visible.length === 0 ? <div className="p-10 text-center text-gray-500">No hay actividades para este filtro.</div> : <div className="divide-y">{visible.map((item) => {
            const overdue = item.estado !== 'completada' && item.estado !== 'cancelada' && item.fecha_limite && item.fecha_limite < new Date().toISOString().slice(0, 10);
            return <article key={item.id} className="p-5 flex flex-col lg:flex-row lg:items-center gap-4"><div className="flex-1 min-w-0"><div className="flex flex-wrap items-center gap-2"><h3 className="font-semibold text-gray-900">{item.titulo}</h3>{overdue && <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-semibold">Vencida</span>}</div>{canSupervise && <p className="text-sm text-teal-700 mt-1">{coachNames.get(item.coach_admin_id) || 'Coach'}</p>}{item.descripcion && <p className="text-sm text-gray-600 mt-2 whitespace-pre-wrap">{item.descripcion}</p>}<p className="text-xs text-gray-500 mt-2">{item.fecha_limite ? `Límite: ${new Date(`${item.fecha_limite}T12:00:00`).toLocaleDateString('es-CL')}` : 'Sin fecha límite'}</p></div><select value={item.estado} onChange={(e) => void updateStatus(item.id, e.target.value as Estado)} className="px-3 py-2 border rounded-lg text-sm" disabled={!canSupervise && item.estado === 'cancelada'}>{Object.entries(labels).map(([value, label]) => <option key={value} value={value} disabled={!canSupervise && value === 'cancelada'}>{label}</option>)}</select></article>;
          })}</div>}
        </div>
      </div>
    </AdminLayout>
  );
}
